import { FC, memo, useEffect, useState } from 'react';
import useAudio from 'react-use/lib/useAudio';
import { Box, Flex } from 'theme-ui';
import { AudioToolProps } from '.';
import { audioTimeLineContainer, skeletonStyle, songStyles } from './style';
import { throttle } from 'throttle-debounce';
import Icon, { IconTypes } from 'shared/Icon';
import { Slider } from 'shared/Slider';
import { formatAudioTime } from 'utils/formatTime';
import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import { useTranslator } from '@picsart/rc/services/localization';
import { usePlayer } from 'utils/PlayerContext';

type SongProps = {
  id: string;
  url: string;
  songName: string;
  artistName?: string;
  duration: number;
  isSelected: boolean;
  active: boolean;
  isPaid: boolean;
  setSongId: (id: string) => void;
  handleSelectSong: AudioToolProps['handleSelectSong'];
  handleUnSelectSong: AudioToolProps['handleUnSelectSong'];
  coverUrl: string;
};

const PaidSymbol: FC = memo(() => {
  const translator = useTranslator();
  return (
    <Tooltip placement={placementsList.bottomStart} content={translator('This_is_a_premium_audio')}>
      <span>
        <Icon className="song-premium-icon" name="gold-circle-gen" />
      </span>
    </Tooltip>
  );
});

export const SongComponent: FC<SongProps> = ({
  songName,
  url,
  isPaid,
  duration,
  artistName,
  active,
  setSongId,
  isSelected,
  handleSelectSong,
  handleUnSelectSong,
  id,
  coverUrl
}) => {
  const [src, setSrc] = useState(null);
  const [audio, state, controls] = useAudio({
    src
  });
  const { pause } = usePlayer();

  useEffect(() => {
    if (active) {
      controls.play();
    } else {
      controls.pause();
    }
  }, [active]);

  const playHandler = () => {
    pause();
    setSongId(id);
    if (!src) {
      setSrc(url);
      return;
    }
    if (state.paused) {
      controls.play();
    } else {
      controls.pause();
    }
  };

  const onAudioTimeLineChange = throttle(50, (value: number) => {
    controls.seek(value);
  });

  const selectSong = () => {
    isSelected
      ? handleUnSelectSong()
      : handleSelectSong({ id, url, songName, isPaid, artistName, duration: duration * 1000 });
  };
  return (
    <div data-test={isPaid ? 'audio_paid' : 'audio_free'} sx={songStyles}>
      <div className="song-container">
        {audio}
        <div className="song-col-1">
          <button
            style={{ backgroundImage: `url(${coverUrl})` }}
            className="song-button-play"
            onClick={playHandler}
          >
            <div className="song-button-play-overlay" />
            <Icon
              className="song-button-play-icon"
              name={state.paused || !active ? 'play-editor' : 'pause-editor'}
              type={IconTypes.White}
            />
          </button>
          <div className="song-info">
            <div data-test="audio_song_name" className="song-text">
              {songName}
            </div>
            <div data-test="audio_artist_name" className="song-artist-name">
              {artistName || 'Мy collection'}
            </div>
          </div>
        </div>
        <div
          className="song-col-2"
          style={{ justifyContent: isPaid ? 'space-between' : 'flex-end' }}
        >
          {isPaid && <PaidSymbol />}
          <div className="song-text" style={{ marginRight: isPaid ? 0 : 4 }}>
            {formatAudioTime(duration)}
          </div>
          <button className="song-add-button" onClick={selectSong} data-test="add_audio">
            {isSelected ? (
              <Icon name="delete-editor" type={IconTypes.Text2} />
            ) : (
              <Icon name="add-editor" type={IconTypes.Text2} />
            )}
          </button>
        </div>
      </div>
      {state.duration !== 0 && active && (
        <Box sx={audioTimeLineContainer}>
          <Slider
            min={0}
            value={state.time}
            className="audioTimeline"
            onChange={onAudioTimeLineChange}
            max={state.duration}
          />
        </Box>
      )}
    </div>
  );
};

export const SongSkeleton = () => (
  <Box sx={{ mb: 6 }}>
    <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <Flex>
        <Box sx={{ ...skeletonStyle, width: 40, height: 40 }} />
        <Box
          sx={{ ml: 4, justifyContent: 'space-around', display: 'flex', flexDirection: 'column' }}
        >
          <Box sx={{ ...skeletonStyle, width: 132, height: 12 }} />
          <Box sx={{ ...skeletonStyle, width: 86, height: 12 }} />
        </Box>
      </Flex>
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: 96
        }}
      >
        <Box sx={{ ...skeletonStyle, width: 32, height: 12, mr: 5 }} />
        <Box sx={{ ...skeletonStyle, width: 32, height: 32 }} />
      </Flex>
    </Flex>
  </Box>
);

export const TagSkeleton = () => (
  <div
    sx={{
      mb: 1,
      mr: 1,
      borderRadius: 'alert',
      borderStyle: 'solid',
      borderColor: 'body',
      borderWidth: 1,
      width: 73,
      height: 43
    }}
  />
);
